import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  fetchProjectMembers,
  fetchInvitableUsers,
  addProjectMember,
  updateProjectMember,
  removeProjectMember,
} from "../../api/projectsApi";

const ROLE_OPTIONS = [
  { value: "viewer", label: "Viewer" },
  { value: "redactor", label: "Redactor" },
  { value: "admin", label: "Admin" },
];

const ROLE_BADGE_COLORS = {
  viewer: "#64748b",
  redactor: "#0ea5e9",
  admin: "#f59e0b",
};

export default function MembersTab({ token, projectId, canManage = false }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Invite state
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [inviteRole, setInviteRole] = useState("viewer");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchDebounceRef = useRef(null);
  const inviteWrapperRef = useRef(null);

  const [editingId, setEditingId] = useState(null);
  const [editRole, setEditRole] = useState("viewer");
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [rowBusy, setRowBusy] = useState(false);

  const loadMembers = useCallback(async () => {
    if (!token || !projectId) return;
    setLoading(true);
    setError("");
    try {
      const list = await fetchProjectMembers(token, projectId);
      setMembers(list || []);
    } catch (err) {
      setError(err?.message || "Unable to load members.");
    } finally {
      setLoading(false);
    }
  }, [token, projectId]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  // Debounced user search for invite
  useEffect(() => {
    if (!canManage || !token || !projectId) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const users = await fetchInvitableUsers(token, projectId, search);
        setSuggestions(users || []);
      } catch {
        setSuggestions([]);
      }
    }, 250);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search, token, projectId, canManage]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (inviteWrapperRef.current && !inviteWrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleInvite = async () => {
    if (!selectedUser || !inviteRole) return;
    setInviteBusy(true);
    setError("");
    try {
      await addProjectMember(token, projectId, selectedUser.id, inviteRole);
      setSelectedUser(null);
      setSearch("");
      setInviteRole("viewer");
      await loadMembers();
    } catch (err) {
      setError(err?.message || "Unable to invite user.");
    } finally {
      setInviteBusy(false);
    }
  };

  const startEdit = (member) => {
    setEditingId(member.id);
    setEditRole(member.role || "viewer");
    setConfirmRemoveId(null);
  };

  const saveEdit = async (member) => {
    setRowBusy(true);
    setError("");
    try {
      await updateProjectMember(token, projectId, member.id, member.user_id, editRole);
      setEditingId(null);
      await loadMembers();
    } catch (err) {
      setError(err?.message || "Unable to update member role.");
    } finally {
      setRowBusy(false);
    }
  };

  const handleRemove = async (memberId) => {
    setRowBusy(true);
    setError("");
    try {
      await removeProjectMember(token, projectId, memberId);
      setConfirmRemoveId(null);
      await loadMembers();
    } catch (err) {
      setError(err?.message || "Unable to remove member.");
    } finally {
      setRowBusy(false);
    }
  };

  const roleBadge = (role) => (
    <span
      className="role-badge"
      style={{ background: ROLE_BADGE_COLORS[role] || "#64748b" }}
    >
      {role || "—"}
    </span>
  );

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      if (a.is_creator) return -1;
      if (b.is_creator) return 1;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [members]);

  return (
    <div className="members-tab">
      <h3 className="members-title">Project members ({members.length})</h3>

      {error && <div className="msg-error">{error}</div>}

      {canManage && (
        <div className="members-invite" ref={inviteWrapperRef}>
          <div className="members-invite-row">
            <div className="members-invite-search">
              <input
                type="text"
                placeholder="Search user by name or email..."
                value={selectedUser ? selectedUser.name + " (" + selectedUser.email + ")" : search}
                onChange={(e) => {
                  setSelectedUser(null);
                  setSearch(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
              />
              {showSuggestions && !selectedUser && suggestions.length > 0 && (
                <ul className="members-suggestions">
                  {suggestions.map((u) => (
                    <li
                      key={u.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setSelectedUser(u);
                        setSearch("");
                        setShowSuggestions(false);
                      }}
                    >
                      <strong>{u.name}</strong>
                      <span className="members-email">{u.email}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <select
              className="members-role-select"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>

            <button
              type="button"
              className="primary"
              onClick={handleInvite}
              disabled={!selectedUser || inviteBusy}
            >
              {inviteBusy ? "Inviting…" : "Invite"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="members-empty">Loading...</div>
      ) : members.length === 0 ? (
        <div className="members-empty">No members yet.</div>
      ) : (
        <ul className="members-list">
          {sortedMembers.map((m) => {
            const isEditing = editingId === m.id;
            const isConfirming = confirmRemoveId === m.id;

            return (
              <li key={m.id} className={"members-item" + (isEditing ? " is-editing" : "")}>
                <div className="members-info">
                  <div className="members-name">
                    {m.name || "—"}
                    {m.is_creator && <span className="members-creator-tag">creator</span>}
                  </div>
                  <div className="members-email">{m.email}</div>
                </div>

                {isEditing ? (
                  <div className="members-actions">
                    <select
                      className="members-role-select"
                      value={editRole}
                      onChange={(e) => setEditRole(e.target.value)}
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="primary"
                      onClick={() => saveEdit(m)}
                      disabled={rowBusy}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => setEditingId(null)}
                      disabled={rowBusy}
                    >
                      Cancel
                    </button>
                  </div>
                ) : isConfirming ? (
                  <div className="members-actions">
                    <button
                      type="button"
                      className="danger"
                      onClick={() => handleRemove(m.id)}
                      disabled={rowBusy}
                    >
                      Confirm remove
                    </button>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => setConfirmRemoveId(null)}
                      disabled={rowBusy}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="members-actions">
                    {roleBadge(m.role)}
                    {canManage && !m.is_creator && (
                      <>
                        <button
                          type="button"
                          className="icon-btn"
                          title="Change role"
                          onClick={() => startEdit(m)}
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          className="icon-btn icon-btn--danger"
                          title="Remove"
                          onClick={() => setConfirmRemoveId(m.id)}
                        >
                          🗑
                        </button>
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
